import { useEffect, useState, createContext, useContext } from "react";
import axios from "axios";
import { API } from "@/App";
import { NavLink, useNavigate, useLocation, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { LayoutDashboard, User, LogOut, Building2, Menu, X } from "lucide-react";
import IntroSplash from "@/components/IntroSplash";

const MemberContext = createContext(null);
export const useMember = () => useContext(MemberContext);

// Kept in sync with MemberPortal.jsx
const COMPANIES = {
  misas:       { label: "Misaş",       title: "Mis-aş Gıda İnş. San. Ve Tic. A.Ş" },
  mis_holding: { label: "Mis Holding", title: "Mis Holding A.Ş." },
  koop:        { label: "Mis Koop.",   title: "S.S. Mis Tüketim Kooperatifi" },
};

const NAV = [
  { to: "/uye", label: "Hisselerim", icon: LayoutDashboard, testid: "mnav-home", end: true },
  { to: "/uye/profil", label: "Profilim", icon: User, testid: "mnav-profile" },
];

export function MemberProtected({ children }) {
  const [state, setState] = useState({ loading: true, member: null });

  const load = async () => {
    const token = localStorage.getItem("member_token");
    if (!token) { setState({ loading: false, member: null }); return; }
    try {
      const r = await axios.get(`${API}/member/me`, { headers: { Authorization: `Bearer ${token}` } });
      setState({ loading: false, member: r.data });
    } catch {
      localStorage.removeItem("member_token");
      setState({ loading: false, member: null });
    }
  };

  useEffect(() => { load(); }, []);

  if (state.loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500 text-sm">Yükleniyor...</div>;
  if (!state.member) return <Navigate to="/uye/giris" replace />;

  const refresh = load;
  return (
    <MemberContext.Provider value={{ member: state.member, refresh }}>
      <MemberShell>{children}</MemberShell>
    </MemberContext.Provider>
  );
}

function SidebarHeader({ singleCompany, myCompanyKeys }) {
  return (
    <div className="px-5 py-6 pr-12 flex items-center gap-3 border-b border-slate-200">
      <div className="h-9 w-9 rounded-md bg-slate-900 text-white flex items-center justify-center shrink-0">
        <Building2 size={18} />
      </div>
      <div className="min-w-0">
        <div className="text-sm font-bold tracking-tight text-slate-900">Ortak Portalı</div>
        <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 truncate"
             title={singleCompany?.title || myCompanyKeys.map(k => COMPANIES[k].label).join(" · ") || "Misland"}>
          {singleCompany
            ? singleCompany.label
            : myCompanyKeys.length > 1
              ? myCompanyKeys.map(k => COMPANIES[k].label).join(" · ")
              : "Misland"}
        </div>
      </div>
    </div>
  );
}

function SidebarInner({ member, singleCompany, myCompanyKeys, onLogout, onNavClick }) {
  return (
    <>
      <SidebarHeader singleCompany={singleCompany} myCompanyKeys={myCompanyKeys} />
      <nav className="p-3 space-y-1 overflow-y-auto flex-1">
        {NAV.map(n => (
          <NavLink key={n.to} to={n.to} end={n.end}
            data-testid={n.testid}
            onClick={onNavClick}
            className={({ isActive }) => `side-link ${isActive ? "active" : ""}`}>
            <n.icon size={17} />{n.label}
          </NavLink>
        ))}
      </nav>
      <div className="mt-auto p-3 border-t border-slate-200">
        <div className="p-2">
          <div className="text-sm font-semibold text-slate-900 truncate" data-testid="member-name">{member.ad_soyad}</div>
          <div className="text-[11px] text-slate-500 truncate font-mono-nums">{member.tc_kimlik}</div>
        </div>
        <button data-testid="member-logout" onClick={onLogout}
          className="w-full mt-2 inline-flex items-center gap-2 justify-center text-sm rounded-md border border-slate-200 text-slate-700 hover:bg-slate-100 py-2">
          <LogOut size={15} /> Çıkış Yap
        </button>
      </div>
    </>
  );
}

function MemberShell({ children }) {
  const { member } = useMember();
  const nav = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const logout = async () => {
    const t = localStorage.getItem("member_token");
    try { await axios.post(`${API}/member/logout`, {}, { headers: t ? { Authorization: `Bearer ${t}` } : {}, withCredentials: true }); } catch (_) {}
    localStorage.removeItem("member_token");
    toast.success("Çıkış yapıldı");
    nav("/uye/giris", { replace: true });
  };

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileOpen]);

  // Only the companies where the member holds shares.
  const myCompanyKeys = ["misas", "mis_holding", "koop"].filter(k => Number(member[`${k}_hisse`] || 0) > 0);
  const singleCompany = myCompanyKeys.length === 1 ? COMPANIES[myCompanyKeys[0]] : null;

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:flex-col w-64 shrink-0 bg-white border-r border-slate-200">
        <SidebarInner
          member={member}
          singleCompany={singleCompany}
          myCompanyKeys={myCompanyKeys}
          onLogout={logout}
        />
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-slate-200 h-14 flex items-center px-3 gap-3">
        <button
          type="button"
          data-testid="mobile-menu-btn"
          onClick={() => setMobileOpen(true)}
          aria-label="Menüyü aç"
          className="h-10 w-10 inline-flex items-center justify-center rounded-md border border-slate-200 text-slate-700 hover:bg-slate-100"
        >
          <Menu size={18} />
        </button>
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-8 w-8 rounded-md bg-slate-900 text-white flex items-center justify-center shrink-0">
            <Building2 size={16} />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-bold tracking-tight text-slate-900 truncate">Ortak Portalı</div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 truncate">
              {singleCompany ? singleCompany.label : (myCompanyKeys.length > 1 ? myCompanyKeys.map(k => COMPANIES[k].label).join(" · ") : "Misland")}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile drawer backdrop */}
      {mobileOpen && (
        <div
          data-testid="mobile-drawer-backdrop"
          onClick={() => setMobileOpen(false)}
          className="md:hidden fixed inset-0 z-50 bg-slate-900/50"
        />
      )}

      {/* Mobile drawer */}
      <aside
        data-testid="mobile-drawer"
        className={`md:hidden fixed top-0 left-0 z-50 h-full w-72 max-w-[85vw] bg-white border-r border-slate-200 flex flex-col shadow-xl transition-transform duration-300 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
        aria-hidden={!mobileOpen}
      >
        <button
          type="button"
          data-testid="mobile-drawer-close"
          onClick={() => setMobileOpen(false)}
          aria-label="Menüyü kapat"
          className="absolute top-3 right-3 h-9 w-9 inline-flex items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
        >
          <X size={18} />
        </button>
        <SidebarInner
          member={member}
          singleCompany={singleCompany}
          myCompanyKeys={myCompanyKeys}
          onLogout={logout}
          onNavClick={() => setMobileOpen(false)}
        />
      </aside>

      <main className="flex-1 min-w-0 pt-14 md:pt-0">
        <div className="p-4 sm:p-6 md:p-10 max-w-[1200px] mx-auto">{children}</div>
      </main>
      <IntroSplash member={member} />
    </div>
  );
}
